import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { usePermissions } from '@/permissions/usePermissions';
import {
  DEFAULT_SEARCH_LIMIT,
  MIN_SEARCH_LENGTH,
  searchableSources,
  searchEverything,
} from '@/store/global-search/global-search.api';
import { collectHits, type SearchHit } from '@/store/global-search/global-search.hits';
import { useVessels } from '@/store/vessels/vessels.store';
import { useDebounce } from '@/utils/use-debounce';

export type GlobalSearchResults = {
  hits: SearchHit[];
  isFetching: boolean;
  /** Every service failed — there is nothing to show and the user should know. */
  isError: boolean;
  /** Services that failed while others answered, for the partial-results note. */
  failed: string[];
  /** True once the debounce has caught up, so "no results" is not shown early. */
  isSettled: boolean;
};

/**
 * Backend hits for the palette. The query is debounced and gated on a minimum
 * length, so typing a word is one request across the seven services rather than
 * one per keystroke.
 *
 * Note: `getRequest` takes no AbortSignal, so react-query cannot cancel an
 * in-flight fan-out. The debounce is what keeps the request count down; a
 * superseded response is discarded by the query key, not aborted.
 */
export const useGlobalSearchResults = (query: string, enabled: boolean): GlobalSearchResults => {
  const trimmed = query.trim();
  const debounced = useDebounce(trimmed);
  const { vessel } = useVessels();
  const { rights } = usePermissions();

  // Which services this account can search at all. Part of the query key: switching to a
  // vessel with a different plan searches a different set and must not read the old answer.
  const sources = useMemo(() => searchableSources(rights), [rights]);

  const canSearch = enabled && debounced.length >= MIN_SEARCH_LENGTH;

  const { data, isFetching, isError } = useQuery({
    queryKey: ['global-search', debounced, DEFAULT_SEARCH_LIMIT, sources],
    queryFn: () => searchEverything(debounced, DEFAULT_SEARCH_LIMIT, sources),
    enabled: canSearch,
    // A repeated search inside a minute is the user reopening the palette to
    // click the row they just missed, not asking a new question.
    staleTime: 60_000,
    // One retry: the fan-out already tolerates a service being down, so a
    // failure here means the whole set failed and retrying three times just
    // makes the panel sit empty for longer.
    retry: 1,
  });

  const hits = useMemo(
    () => (data ? collectHits(data, { vesselId: vessel?.id }) : []),
    [data, vessel?.id],
  );

  return {
    hits,
    isFetching: canSearch && isFetching,
    // Only an error when nothing came back at all; a partial answer is a
    // success with a note.
    isError: isError && !data,
    failed: data?.failed ?? [],
    isSettled: !canSearch || debounced === trimmed,
  };
};
